/**
 * Client-side helper for the server-side Claude vision card identification API.
 *
 * identifyCardWithVision — uploads a photo URI to /api/card-vision/identify
 * and returns structured card metadata extracted by the vision model.
 *
 * Two modes:
 *   - Locked-set (setName provided): Claude is told the set is already known
 *     and focuses only on reading the collector number, card name, HP, and
 *     types. Faster and more accurate because Claude doesn't waste attention
 *     budget re-identifying the set.
 *   - Free-scan (no setName): Claude uses a set-first extraction strategy,
 *     reading the expansion symbol / set name before the collector number.
 *
 * Returns null on any network or server error so callers can fall back to
 * hash-only or empty-picker gracefully.
 *
 * This is a single-shot call (no streaming, no conversation history).
 * It is intended as a fallback when dHash confidence is MEDIUM, LOW, or
 * returns no matches — NOT for every scan (HIGH hash results skip this call
 * entirely to avoid unnecessary cost).
 */

import { API_BASE, apiFetch } from "@/lib/apiClient";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface VisionCardResult {
  /** Full printed set name (e.g. "Prismatic Evolutions", "Base Set"). null if unreadable. */
  setName: string | null;
  /** Series/era (e.g. "Scarlet & Violet", "XY"). null if unreadable. */
  series: string | null;
  /** Machine-readable set code (e.g. "sv8pt5"). null if not clearly readable. */
  setCode: string | null;
  name: string | null;
  number: string | null;
  rarity: string | null;
  hp: number | null;
  types: string[];
  /** Confidence the resolved set/printing is correct (TNK-194). Only present
   *  on the free-scan path (omitted in locked-set mode). "high" → safe to
   *  auto-accept; "medium"/"low" → surface for operator review. */
  confidence?: "high" | "medium" | "low";
  /** Convenience flag (= confidence !== "high"). When true, have the operator
   *  confirm the card before committing it. */
  needsReview?: boolean;
  /** TNK-194 — the resolved catalog printing's stock image, so the scan review
   *  shows a MATCH thumbnail (scan items have no server-persisted catalog). */
  matchImageUrl?: string;
  matchImageLargeUrl?: string;
}

// ── Vision identify ────────────────────────────────────────────────────────────

/**
 * Upload a card photo to the server and return the structured card metadata
 * extracted by Claude vision.
 *
 * When `setName` is provided the server switches to locked-set mode: Claude
 * is told the set is already known and focuses only on reading the collector
 * number, card name, HP, and types (cheaper, more accurate). When omitted the
 * server uses the free-scan set-first extraction strategy.
 *
 * Returns null on any network or server error so callers degrade gracefully.
 *
 * @param image   - Local camera/file URI **or** a pre-built Blob (e.g. from a
 *                  server-side preprocessed image returned by `/card-hash/match`).
 *                  When a Blob is passed it is uploaded directly as multipart
 *                  without any additional file-system access.
 * @param setId   - Optional set ID hint (used for server-side caching/logging)
 * @param setName - Human-readable set name (e.g. "Prismatic Evolutions").
 *                  Enables locked-set mode when provided.
 */
export async function identifyCardWithVision(
  image: string | Blob,
  setId: string | null = null,
  setName: string | null = null,
  // TNK-216 — the operator-selected game drives the server's vision prompt +
  // which catalog the result resolves against (Pokémon vs Magic/Scryfall).
  game = "Pokemon",
): Promise<VisionCardResult | null> {
  try {
    const formData = new FormData();
    if (typeof image === "string") {
      formData.append("image", {
        uri: image,
        name: "card.jpg",
        type: "image/jpeg",
      } as unknown as Blob);
    } else {
      formData.append("image", image, "preprocessed.jpg");
    }
    if (setId) formData.append("setId", setId);
    if (setName) formData.append("setName", setName);
    formData.append("game", game);

    const resp = await apiFetch(`${API_BASE}/api/card-vision/identify`, {
      method: "POST",
      body: formData,
    });

    if (!resp.ok) return null;
    return (await resp.json()) as VisionCardResult;
  } catch {
    return null;
  }
}

// ── Visual match (reference-image comparison) ──────────────────────────────────

/**
 * One candidate card to compare against the scanned photo. `imageUrl` must be a
 * pokemontcg.io reference image (the server enforces an allowlist).
 */
export interface VisionMatchCandidate {
  id: string;
  name: string;
  number: string;
  imageUrl: string;
}

/**
 * Result of a visual-match call. `matchedCardId` is the id of the candidate
 * Claude picked (or null if none matched). `confidence` reflects Claude's own
 * self-assessment — callers should typically only auto-dispatch on "high".
 */
export interface VisionMatchResult {
  matchedCardId: string | null;
  confidence: "high" | "medium" | "low";
  reasoning: string;
}

/**
 * Send the scanned photo + a small list of candidate reference images to the
 * server, which proxies them to Claude vision for visual comparison. Used as a
 * fallback when dHash returns MEDIUM-confidence candidates: instead of asking
 * the user to pick from a modal, we let Claude pick by comparing artwork.
 *
 * Returns null on any network/server error so callers degrade gracefully.
 *
 * @param image       Local camera/file URI **or** a pre-built Blob (e.g. the
 *                    server-preprocessed crop returned by `/card-hash/match`).
 * @param candidates  2–3 candidate cards with pokemontcg.io reference URLs.
 *                    Fewer than 2 valid candidates → server returns a
 *                    low-confidence 200 result (graceful degradation).
 */
/** Hard upper bound for a single visual-match call. Beyond this the scan
 * pipeline must continue without it to avoid stalling recognition. */
const VISION_MATCH_TIMEOUT_MS = 4_000;

export async function matchCardByVision(
  image: string | Blob,
  candidates: VisionMatchCandidate[],
): Promise<VisionMatchResult | null> {
  if (candidates.length < 2) return null;
  try {
    const formData = new FormData();
    if (typeof image === "string") {
      formData.append("image", {
        uri: image,
        name: "card.jpg",
        type: "image/jpeg",
      } as unknown as Blob);
    } else {
      formData.append("image", image, "preprocessed.jpg");
    }
    formData.append("candidates", JSON.stringify(candidates));

    // Bound the call with an AbortController so a stalled server / slow
    // network never blocks the scan pipeline indefinitely.
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      VISION_MATCH_TIMEOUT_MS,
    );
    try {
      const resp = await apiFetch(`${API_BASE}/api/card-vision/match`, {
        method: "POST",
        body: formData,
        signal: controller.signal,
      });
      if (!resp.ok) return null;
      return (await resp.json()) as VisionMatchResult;
    } finally {
      clearTimeout(timeoutId);
    }
  } catch {
    return null;
  }
}
